/**
 * 2D Canvas 知识图谱渲染组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 * 
 * 使用类似 D3 的力导向模拟
 */

import { useRef, useEffect, useCallback, useState } from 'react'
import type { GraphNode, GraphEdge } from '../../store/graphStore'

interface GraphCanvas2DProps {
  nodes: GraphNode[]
  edges: GraphEdge[]
  onNodeHover?: (node: GraphNode | null, position: { x: number; y: number }) => void
  onNodeClick?: (node: GraphNode) => void
  className?: string
}

// 内部节点数据结构
interface SimNode {
  id: string
  name: string
  x: number
  y: number
  vx: number
  vy: number
  fx: number | null  // 固定位置（拖拽时使用）
  fy: number | null
  radius: number
  color: string
  opacity: number
  hasGlow: boolean
  node: GraphNode
}

// 颜色配置
const TYPE_COLORS: Record<string, string> = {
  User: '#AF52DE',
  Person: '#007AFF',
  Name: '#007AFF',
  Family: '#FF2D55',
  Friend: '#34C759',
  City: '#5AC8FA',
  Hobby: '#AF52DE',
  Occupation: '#FF9500',
  Company: '#FF9500',
  Food: '#FF9500',
  FavoriteFood: '#FF9500',
  Pet: '#FFCC00',
  Event: '#5856D6',
  Other: '#8E8E93',
}

const getTypeColor = (type: string): string => TYPE_COLORS[type] || TYPE_COLORS.Other

export default function GraphCanvas2D({
  nodes,
  edges,
  onNodeHover,
  onNodeClick,
  className = '',
}: GraphCanvas2DProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return true
  })
  
  // 模拟数据（使用 ref 避免重新渲染）
  const simNodesRef = useRef<SimNode[]>([])
  const animationRef = useRef<number>(0)
  
  // 模拟参数（Apple Game Center 风格漂浮）
  const alphaRef = useRef(1)
  const alphaTargetRef = useRef(0.02)  // 保持微小的持续运动
  const alphaDecayRef = useRef(0.015)  // 更慢的衰减
  const velocityDecayRef = useRef(0.92)  // 更高的保持（更慢停止）
  const timeRef = useRef(0)  // 用于正弦波漂浮
  
  // 交互状态
  const draggedNodeRef = useRef<SimNode | null>(null)
  const hoveredNodeRef = useRef<SimNode | null>(null)
  const offsetRef = useRef({ x: 0, y: 0 })
  const scaleRef = useRef(1)
  const isPanningRef = useRef(false)
  const panStartRef = useRef({ x: 0, y: 0 })

  // 初始化节点数据
  useEffect(() => {
    // 调试日志：打印收到的数据
    console.log('[GraphCanvas2D] 收到数据:', JSON.stringify({
      nodeCount: nodes.length,
      edgeCount: edges.length,
      nodes: nodes.map(n => ({ id: n.id, name: n.name, type: n.type })),
      edges: edges.map(e => ({ 
        id: e.id, 
        sourceId: e.sourceId, 
        targetId: e.targetId,
        sourceName: e.sourceName,
        targetName: e.targetName,
        relation: e.relation 
      })),
    }, null, 2))
    
    if (nodes.length === 0) {
      simNodesRef.current = []
      return
    }
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const width = canvas.offsetWidth || 800
    const height = canvas.offsetHeight || 600
    const centerX = width / 2
    const centerY = height / 2
    
    // 创建或更新节点
    const existingMap = new Map(simNodesRef.current.map(n => [n.id, n]))
    const newSimNodes: SimNode[] = []
    
    nodes.forEach((node, index) => {
      const existing = existingMap.get(node.id)
      if (existing) {
        // 保留位置，更新其他属性
        newSimNodes.push({
          ...existing,
          name: node.name,
          radius: node.radius,
          color: getTypeColor(node.type),
          opacity: node.opacity,
          hasGlow: node.hasGlow,
          node,
        })
      } else {
        // 新节点：环形布局
        const angle = (index / nodes.length) * Math.PI * 2
        const layoutRadius = Math.min(width, height) * 0.3
        newSimNodes.push({
          id: node.id,
          name: node.name,
          x: centerX + Math.cos(angle) * layoutRadius,
          y: centerY + Math.sin(angle) * layoutRadius,
          vx: 0,
          vy: 0,
          fx: null,
          fy: null,
          radius: node.radius,
          color: getTypeColor(node.type),
          opacity: node.opacity,
          hasGlow: node.hasGlow,
          node,
        })
      }
    })
    
    simNodesRef.current = newSimNodes
    
    // 重新启动模拟
    alphaRef.current = 1
    alphaTargetRef.current = 0
    
  }, [nodes])

  // 力导向模拟（Apple Game Center 风格漂浮）
  const simulate = useCallback(() => {
    const simNodes = simNodesRef.current
    if (simNodes.length === 0) return
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const width = canvas.offsetWidth || 800
    const height = canvas.offsetHeight || 600
    
    // 时间递增（用于正弦波漂浮）
    timeRef.current += 0.016  // ~60fps
    
    // Alpha 衰减
    alphaRef.current += (alphaTargetRef.current - alphaRef.current) * alphaDecayRef.current
    
    // 始终进行模拟（包括轻柔漂浮）
    const alpha = Math.max(alphaRef.current, 0.02)  // 最小保持 0.02 的力
    
    if (true) {  // 始终运行
      
      // 斥力（charge force）- strength: -300
      for (let i = 0; i < simNodes.length; i++) {
        for (let j = i + 1; j < simNodes.length; j++) {
          const ni = simNodes[i]
          const nj = simNodes[j]
          
          const dx = nj.x - ni.x
          const dy = nj.y - ni.y
          const dist = Math.sqrt(dx * dx + dy * dy) || 1
          const force = (-300 / (dist * dist)) * alpha
          
          const fx = (dx / dist) * force
          const fy = (dy / dist) * force
          
          ni.vx -= fx
          ni.vy -= fy
          nj.vx += fx
          nj.vy += fy
        }
      }
      
      // 引力（link force）- distance: 120
      // 首次打印匹配日志
      if (alphaRef.current > 0.99) {
        console.log('[GraphCanvas2D] 开始边匹配，simNodes:', JSON.stringify(simNodes.map(n => ({ id: n.id, name: n.name }))))
      }
      
      edges.forEach((edge, idx) => {
        // 用 entity ID 匹配，而非 name，避免名称不一致导致匹配失败
        const source = simNodes.find(n => n.id === edge.sourceId)
        const target = simNodes.find(n => n.id === edge.targetId)
        
        // 首次打印每条边的匹配结果
        if (alphaRef.current > 0.99 && idx < 5) {
          console.log(`[GraphCanvas2D] 边 ${idx}:`, JSON.stringify({
            edgeSourceId: edge.sourceId,
            edgeTargetId: edge.targetId,
            edgeSourceName: edge.sourceName,
            edgeTargetName: edge.targetName,
            foundSource: source ? { id: source.id, name: source.name } : null,
            foundTarget: target ? { id: target.id, name: target.name } : null,
          }))
        }
        
        if (source && target) {
          const dx = target.x - source.x
          const dy = target.y - source.y
          const dist = Math.sqrt(dx * dx + dy * dy) || 1
          const force = (dist - 120) * 0.03 * alpha
          
          const fx = (dx / dist) * force
          const fy = (dy / dist) * force
          
          source.vx += fx
          source.vy += fy
          target.vx -= fx
          target.vy -= fy
        }
      })
      
      // 中心引力（center force）
      simNodes.forEach((node) => {
        node.vx += (width / 2 - node.x) * 0.01 * alpha
        node.vy += (height / 2 - node.y) * 0.01 * alpha
      })
      
      // 碰撞检测（collision force）
      for (let i = 0; i < simNodes.length; i++) {
        for (let j = i + 1; j < simNodes.length; j++) {
          const ni = simNodes[i]
          const nj = simNodes[j]
          
          const dx = nj.x - ni.x
          const dy = nj.y - ni.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          const minDist = ni.radius + nj.radius + 10
          
          if (dist < minDist && dist > 0) {
            const overlap = (minDist - dist) / 2
            const nx = dx / dist
            const ny = dy / dist
            
            if (ni.fx === null) {
              ni.x -= nx * overlap
              ni.y -= ny * overlap
            }
            if (nj.fx === null) {
              nj.x += nx * overlap
              nj.y += ny * overlap
            }
          }
        }
      }
      
      // 应用速度并衰减 + Apple 风格轻柔漂浮
      simNodes.forEach((node, index) => {
        // 添加轻柔的正弦波漂浮（每个节点有不同的相位）
        const phase = index * 0.7
        const floatX = Math.sin(timeRef.current * 0.5 + phase) * 0.15
        const floatY = Math.cos(timeRef.current * 0.4 + phase * 1.3) * 0.12
        
        if (node.fx !== null) {
          // 固定位置（拖拽中）
          node.x = node.fx
          node.vx = 0
        } else {
          node.vx *= velocityDecayRef.current
          node.vx += floatX
          node.x += node.vx
        }
        
        if (node.fy !== null) {
          node.y = node.fy
          node.vy = 0
        } else {
          node.vy *= velocityDecayRef.current
          node.vy += floatY
          node.y += node.vy
        }
        
        // 边界约束
        node.x = Math.max(node.radius, Math.min(width - node.radius, node.x))
        node.y = Math.max(node.radius, Math.min(height - node.radius, node.y))
      })
    }
  }, [edges])

  // 绘制函数
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    
    // 设置 canvas 大小
    if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.scale(dpr, dpr)
    }
    
    const width = rect.width
    const height = rect.height
    const simNodes = simNodesRef.current
    const offset = offsetRef.current
    const scale = scaleRef.current
    
    // 清空画布
    ctx.fillStyle = isDark ? '#1C1C1E' : '#FFFFFF'
    ctx.fillRect(0, 0, width, height)
    
    // 应用变换
    ctx.save()
    ctx.translate(offset.x, offset.y)
    ctx.scale(scale, scale)
    
    // 绘制关系线
    let drawnEdges = 0
    let skippedEdges = 0
    edges.forEach((edge) => {
      // 用 entity ID 匹配，而非 name，避免名称不一致导致匹配失败
      const source = simNodes.find(n => n.id === edge.sourceId)
      const target = simNodes.find(n => n.id === edge.targetId)
      
      if (!source || !target) {
        skippedEdges++
        return
      }
      drawnEdges++
      
      // 计算控制点（贝塞尔曲线）
      const midX = (source.x + target.x) / 2
      const midY = (source.y + target.y) / 2
      const dx = target.x - source.x
      const dy = target.y - source.y
      const perpX = -dy * 0.2
      const perpY = dx * 0.2
      const ctrlX = midX + perpX
      const ctrlY = midY + perpY
      
      // 线条样式（更细更优雅）
      ctx.strokeStyle = isDark 
        ? `rgba(180, 180, 180, ${Math.min(0.6, edge.opacity + 0.1)})` 
        : `rgba(100, 100, 100, ${Math.min(0.5, edge.opacity + 0.1)})`
      ctx.lineWidth = 1
      ctx.setLineDash(edge.lineStyle === 'dashed' ? [6, 3] : edge.lineStyle === 'dotted' ? [2, 3] : [])
      
      ctx.beginPath()
      ctx.moveTo(source.x, source.y)
      ctx.quadraticCurveTo(ctrlX, ctrlY, target.x, target.y)
      ctx.stroke()
      ctx.setLineDash([])
      
      // 关系标签 - 智能字体大小
      const distance = Math.sqrt(dx * dx + dy * dy)  // 计算边的长度
      const relationFontSize = Math.max(10, Math.min(14, Math.sqrt(distance) * 0.8))  // 根据边长度调整字体大小
      ctx.font = `bold ${relationFontSize}px -apple-system, sans-serif`
      
      // 关系名称智能截断
      const getRelationDisplayName = (relation: string) => {
        const maxLength = 12  // 关系名称最大长度
        return relation.length > maxLength ? relation.slice(0, maxLength) + '…' : relation
      }
      
      const displayRelation = getRelationDisplayName(edge.relation)
      const textWidth = ctx.measureText(displayRelation).width
      const padding = 6
      
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)'
      ctx.beginPath()
      ctx.roundRect(ctrlX - textWidth / 2 - padding, ctrlY - 9, textWidth + padding * 2, 18, 4)
      ctx.fill()
      
      ctx.fillStyle = isDark ? '#000000' : '#FFFFFF'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(displayRelation, ctrlX, ctrlY)
    })
    
    // 绘制节点（Apple Game Center 风格气泡效果）
    simNodes.forEach((node) => {
      const isHovered = hoveredNodeRef.current?.id === node.id
      const radius = node.radius * (isHovered ? 1.08 : 1)
      
      // 解析颜色为 RGB
      const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 100, g: 100, b: 100 }
      }
      const rgb = hexToRgb(node.color)
      
      // 外发光（柔和）
      ctx.save()
      ctx.shadowColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4)`
      ctx.shadowBlur = radius * 0.5
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2)
      ctx.fillStyle = 'transparent'
      ctx.fill()
      ctx.restore()
      
      // 主体渐变（Apple 风格 - 从亮到暗的径向渐变）
      const mainGradient = ctx.createRadialGradient(
        node.x - radius * 0.35, node.y - radius * 0.35, 0,
        node.x, node.y, radius * 1.1
      )
      // 高光区域（更亮的颜色）
      const lighterColor = `rgba(${Math.min(255, rgb.r + 60)}, ${Math.min(255, rgb.g + 60)}, ${Math.min(255, rgb.b + 60)}, 0.95)`
      const mainColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.85)`
      const darkerColor = `rgba(${Math.max(0, rgb.r - 40)}, ${Math.max(0, rgb.g - 40)}, ${Math.max(0, rgb.b - 40)}, 0.9)`
      
      mainGradient.addColorStop(0, lighterColor)
      mainGradient.addColorStop(0.4, mainColor)
      mainGradient.addColorStop(1, darkerColor)
      
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2)
      ctx.fillStyle = mainGradient
      ctx.fill()
      
      // 顶部高光（玻璃质感）
      const highlightGradient = ctx.createRadialGradient(
        node.x - radius * 0.25, node.y - radius * 0.4, 0,
        node.x - radius * 0.1, node.y - radius * 0.2, radius * 0.6
      )
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.7)')
      highlightGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.2)')
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
      
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2)
      ctx.fillStyle = highlightGradient
      ctx.fill()
      
      // 底部反光（环境光）
      const bottomGradient = ctx.createRadialGradient(
        node.x + radius * 0.2, node.y + radius * 0.5, 0,
        node.x, node.y + radius * 0.3, radius * 0.8
      )
      bottomGradient.addColorStop(0, 'rgba(255, 255, 255, 0.15)')
      bottomGradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
      
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2)
      ctx.fillStyle = bottomGradient
      ctx.fill()
      
      // 节点内文字 - 智能截断
      const getDisplayName = (name: string, radius: number) => {
        // 根据节点大小调整字符限制
        const maxChars = radius > 25 ? 8 : radius > 20 ? 6 : radius > 15 ? 5 : 4
        
        // 中文字符占用更多空间，需要更保守的截断
        const isChinese = /[\u4e00-\u9fa5]/.test(name)
        const actualLimit = isChinese ? Math.max(2, Math.floor(maxChars * 0.7)) : maxChars
        
        return name.length > actualLimit ? name.slice(0, actualLimit) + '…' : name
      }
      
      const name = getDisplayName(node.name, radius)
      const fontSize = Math.max(12, Math.min(18, radius * 0.5))  // 提升最小字体到12px，最大18px
      ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`
      ctx.fillStyle = '#FFFFFF'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.shadowColor = 'rgba(0,0,0,0.4)'
      ctx.shadowBlur = 3
      ctx.shadowOffsetY = 1
      ctx.fillText(name, node.x, node.y)
      ctx.shadowBlur = 0
      ctx.shadowOffsetY = 0
    })
    
    // 每隔一段时间打印一次统计（只在首次输出）
    if (drawnEdges === 0 && skippedEdges > 0) {
      console.log('[GraphCanvas2D] 绘制统计 - 边全部跳过!', JSON.stringify({ 
        totalEdges: edges.length, 
        drawnEdges, 
        skippedEdges,
        nodeCount: simNodes.length
      }))
    }
    
    ctx.restore()
  }, [edges, isDark])

  // 动画循环
  useEffect(() => {
    const animate = () => {
      simulate()
      draw()
      animationRef.current = requestAnimationFrame(animate)
    }
    
    animationRef.current = requestAnimationFrame(animate)
    
    return () => {
      cancelAnimationFrame(animationRef.current)
    }
  }, [simulate, draw])

  // 获取鼠标位置
  const getMousePos = useCallback((e: React.MouseEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    
    const rect = canvas.getBoundingClientRect()
    const offset = offsetRef.current
    const scale = scaleRef.current
    
    return {
      x: (e.clientX - rect.left - offset.x) / scale,
      y: (e.clientY - rect.top - offset.y) / scale,
    }
  }, [])

  // 查找鼠标位置的节点
  const findNodeAtPosition = useCallback((x: number, y: number): SimNode | null => {
    const simNodes = simNodesRef.current
    
    for (let i = simNodes.length - 1; i >= 0; i--) {
      const node = simNodes[i]
      const dx = x - node.x
      const dy = y - node.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      
      if (dist <= node.radius) {
        return node
      }
    }
    
    return null
  }, [])

  // 鼠标按下
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const pos = getMousePos(e)
    const node = findNodeAtPosition(pos.x, pos.y)
    
    if (node) {
      // 开始拖拽节点
      draggedNodeRef.current = node
      node.fx = node.x
      node.fy = node.y
      
      // 重新激活模拟（类似 D3 的 alphaTarget(0.3).restart()）
      alphaRef.current = Math.max(alphaRef.current, 0.3)
      alphaTargetRef.current = 0.3
    } else if (e.button === 0) {
      // 开始平移
      isPanningRef.current = true
      panStartRef.current = { 
        x: e.clientX - offsetRef.current.x, 
        y: e.clientY - offsetRef.current.y 
      }
    }
  }, [getMousePos, findNodeAtPosition])

  // 鼠标移动
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const pos = getMousePos(e)
    
    if (draggedNodeRef.current) {
      // 拖拽节点 - 更新固定位置
      draggedNodeRef.current.fx = pos.x
      draggedNodeRef.current.fy = pos.y
    } else if (isPanningRef.current) {
      // 平移画布
      offsetRef.current = {
        x: e.clientX - panStartRef.current.x,
        y: e.clientY - panStartRef.current.y,
      }
    } else {
      // Hover 检测
      const node = findNodeAtPosition(pos.x, pos.y)
      
      if (node !== hoveredNodeRef.current) {
        hoveredNodeRef.current = node
        
        if (onNodeHover) {
          const canvas = canvasRef.current
          if (canvas) {
            const rect = canvas.getBoundingClientRect()
            onNodeHover(
              node?.node || null,
              { x: e.clientX - rect.left, y: e.clientY - rect.top }
            )
          }
        }
      }
    }
  }, [getMousePos, findNodeAtPosition, onNodeHover])

  // 鼠标松开
  const handleMouseUp = useCallback(() => {
    if (draggedNodeRef.current) {
      // 释放固定位置（类似 D3 的 d.fx = null）
      draggedNodeRef.current.fx = null
      draggedNodeRef.current.fy = null
      draggedNodeRef.current = null
      
      // 让模拟自然衰减（类似 D3 的 alphaTarget(0)）
      alphaTargetRef.current = 0
    }
    
    isPanningRef.current = false
  }, [])

  // 滚轮缩放
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    const newScale = Math.max(0.3, Math.min(3, scaleRef.current * delta))
    
    const canvas = canvasRef.current
    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top
      
      offsetRef.current = {
        x: mouseX - (mouseX - offsetRef.current.x) * (newScale / scaleRef.current),
        y: mouseY - (mouseY - offsetRef.current.y) * (newScale / scaleRef.current),
      }
    }
    
    scaleRef.current = newScale
  }, [])

  // 双击重置
  const handleDoubleClick = useCallback(() => {
    offsetRef.current = { x: 0, y: 0 }
    scaleRef.current = 1
    alphaRef.current = 1
    alphaTargetRef.current = 0
  }, [])

  // 点击
  const handleClick = useCallback((e: React.MouseEvent) => {
    const pos = getMousePos(e)
    const node = findNodeAtPosition(pos.x, pos.y)
    
    if (node && onNodeClick) {
      onNodeClick(node.node)
    }
  }, [getMousePos, findNodeAtPosition, onNodeClick])

  return (
    <div ref={containerRef} className={`relative w-full h-full ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ cursor: draggedNodeRef.current ? 'grabbing' : 'grab' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
        onClick={handleClick}
      />
    </div>
  )
}
